<template>
  <v-card class="ma-1 pb-6">
    <div class="d-flex flex-row items-center flex-wrap gap-2 align-items-center justify-content-between mb-2">
      <div class="d-flex flex-row align-items-center gap-2">
        <v-btn
          icon
          to="/utils"
          class="mr-2 ml-1"
          title="Back to Utilities"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <v-select
          v-model="showLimit"
          :items="['All', '1', '0.9', '0.8', '0.7', '0.6', '0.5', '0.4', '0.3', '0.2', '0.1']"
          label="Show "
          title="Do not show structures if no similarity greater than"
          @change="showLimitChange"
          style="max-width: 5rem"
          hide-details
        />

        <v-select
          v-model="selectedHazards"
          v-b-tooltip
          :items="hazards"
          label="Hazard Rating:"
          multiple
          title="Filter by Hazard Rating"
          @change="showLimitChange"
          style="max-width: 15rem"
          hide-details
        />
      </div>

      <div class="d-flex flex-row items-center gap-2 align-items-center">
        <v-switch
          v-model="showImage"
          label="Show structure image"
          title="Show structure image"
          hide-details
        />

        <v-switch
          v-model="sortS"
          label="Sort"
          title="Sort"
          hide-details
        />

        <v-btn
          v-b-tooltip
          title="Remove selected structures from matrix"
          :disabled="selectedStructures.length === 0"
          @click="onRemove()"
          aria-label="Remove selected structures from matrix"
        >
          Remove
        </v-btn>

        <div class="d-flex flex-row justify-end gap-2">
          <v-chip
            title="Selected"
            small
          >
            {{ this.selectedStructures.length }}
          </v-chip>
          /
          <v-chip
            title="Filtered"
            small
          >
            {{ filteredCount }}
          </v-chip>
          /
          <v-chip
            title="Total"
            small
          >
            {{ this.similarity.order ? similarity.order.length : 0 }}
          </v-chip>
        </div>

        <v-btn-toggle dense>
          <v-btn
            v-b-tooltip
            title="Export to CSV"
            @click="onExport('CSV')"
            aria-label="Export to CSV"
          >
            <v-icon>mdi-file-delimited-outline</v-icon>
          </v-btn>
          <v-btn
            v-b-tooltip
            title="Export to XLSX"
            @click="onExport('XLSX')"
            aria-label="Export to XLSX"
          >
            <v-icon>mdi-file-excel-outline</v-icon>
          </v-btn>
        </v-btn-toggle>

        <v-btn-toggle dense>
          <v-btn
            v-b-tooltip
            title="Select All"
            @click="onSelectAllStructures"
            :disabled="selectedStructures.length === filteredCount"
            aria-label="Select All"
          >
            <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
          </v-btn>
          <v-btn
            v-b-tooltip
            title="Select None"
            @click="onClearSelection"
            :disabled="selectedStructures.length === 0"
            aria-label="Select None"
          >
            <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
          </v-btn>
        </v-btn-toggle>

        <TransferMenu
          :records="recordsForTransfer"
          only-icon
        />
      </div>
    </div>

    <!--v-container style="overflow-x: scroll;width: 100%" fluid-->
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <div class="table-container">
      <table class="table table-bordered table-records">
        <thead class="table-secondary">
          <tr>
            <th class="sticky-col" />
            <th
              v-for="entry in shownSimilarity"
              :key="getChemicalDomId(entry.item.chemical)"
            >
              <ChemCard
                :item="entry.item.chemical"
                :show-hazard="true"
                :show-image="showImage"
                sufix="row"
                show-check
                @check-click="onStructureCheckboxClick"
              />
            </th>
          </tr>
        </thead>
        <template v-for="entry in shownSimilarity">
          <tbody :key="getChemicalDomId(entry.item.chemical)">
            <tr>
              <td class="sticky-col">
                <ChemCard
                  :item="entry.item.chemical"
                  :show-hazard="true"
                  :show-image="showImage"
                  sufix="col"
                  show-check
                  @check-click="onStructureCheckboxClick"
                />
              </td>

              <template v-for="(cell, colIndex) in similarity.similarity[entry.originalIndex]">
                <td
                  v-if="similarity.order[colIndex].show"
                  :key="getChemicalDomId(similarity.order[colIndex].chemical)"
                  :class="entry.originalIndex === colIndex ? 'ybc11' : cell.cl"
                >
                  {{ entry.originalIndex === colIndex ? 1 : cell.sim }}
                </td>
              </template>
            </tr>
          </tbody>
        </template>
      </table>
    </div>
  </v-card>
</template>

<script>
import { download, getDatedFilename, getErrorMessage, RESOLVER_URL } from '@/main'
import { showErrorToast, showWarningToast } from '@/utils/toast'
import ChemCard from '@/components/chemical/ChemCard.vue'
import { mapGetters } from 'vuex'
import TransferMenu from '@/components/TransferMenu.vue'
import { cloneDeep } from 'lodash-es'
import { getChemicalDomId } from '@/utils/chemical.js'

export default {
  components: { TransferMenu, ChemCard },

  data: () => ({
    similarity: {},
    loading: false,
    showImage: false,
    sortS: false,
    showLimit: 'All',
    hazards: ['VH', 'H', 'M', 'L', 'I', 'ND', 'Other'],
    selectedHazards: ['VH', 'H', 'M', 'L', 'I', 'ND', 'Other'],
    selectedStructures: [],
    getChemicalDomId,
  }),

  computed: {
    ...mapGetters(['getCheckedStructures']),
    getNonMarkushCheckedStructures() {
      return this.getCheckedStructures.filter((chemical) => chemical.smiles && !chemical.smiles.includes('*'))
    },
    filteredCount() {
      if (!this.similarity.order || this.similarity.order.length === 0) return 0
      return this.similarity.order.filter((item) => item.show).length
    },
    recordsForTransfer() {
      return cloneDeep(this.selectedStructures)
    },
    shownSimilarity() {
      if (!this.similarity.order || this.similarity.order.length === 0) return []
      return this.similarity.order
        .map((item, originalIndex) => ({ item, originalIndex }))
        .filter(({ item }) => item.show)
    },
  },

  watch: {
    sortS: {
      handler() {
        this.similarity = {}
        this.getSimilarity(true)
      },
    },
  },

  methods: {
    onExport(format) {
      this.loading = true
      this.$axios
        .post(
          `${RESOLVER_URL}/exportsimilaritymap?format=${format}&limit=${this.showLimit}&sort=${this.sortS}&showImage=${this.showImage}`,
          {
            chemicals: this.getNonMarkushCheckedStructures,
          },
          { responseType: 'blob' },
        )
        .then((response) => {
          let filename = getDatedFilename('exportsimilarity', format.toLowerCase())
          download(filename, response.data, format.toLowerCase())
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during export' })
        })
        .finally(() => {
          this.loading = false
        })
    },

    showLimitChange() {
      if (!this.similarity.order || this.similarity.order.length === 0) {
        return
      }
      this.similarity.order.forEach((o) => {
        o.show =
          (this.showLimit === 'All' || o.max >= this.showLimit) &&
          (o.chemical.hazard || this.selectedHazards.indexOf('Other') !== -1) &&
          (!o.chemical.hazard ||
            this.selectedHazards.indexOf(o.chemical.hazard) !== -1 ||
            (this.hazards.indexOf(o.chemical.hazard) === -1 && this.selectedHazards.indexOf('Other') !== -1))

        const pos = this.selectedStructures.findIndex((item) => o.chemical.chemId === item.chemId)
        if (!o.show && pos !== -1) {
          o.chemical.checked = false
          this.selectedStructures.splice(pos, 1)
        }
      })
    },

    getSimilarity(setselected) {
      this.loading = true
      this.$axios
        .post(`${RESOLVER_URL}/getsimilaritymap?sort=` + this.sortS, {
          chemicals: this.getNonMarkushCheckedStructures,
        })
        .then((response) => {
          let t = this.selectedStructures
          this.selectedStructures = []
          response.data.order.forEach((o) => {
            o.show = this.showLimit === 'All' || o.max >= this.showLimit
            o.chemical.checked = false
            if (setselected) {
              const pos = t.findIndex((item) => o.chemical.chemId === item.chemId)
              if (o.show && pos !== -1) {
                o.chemical.checked = true
                this.selectedStructures.push(o.chemical)
              }
            }
          })
          this.$set(this, 'similarity', response.data)
          const excludedCount = this.getCheckedStructures.length - this.getNonMarkushCheckedStructures.length
          if (excludedCount !== 0) {
            const warningMessage = `${excludedCount} of ${this.getCheckedStructures.length} structures were excluded from the matrix`
            showWarningToast(warningMessage)
          }
        })
        .catch((error) => {
          showErrorToast(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onStructureCheckboxClick(structure) {
      const pos = this.selectedStructures.findIndex((item) => structure.chemId === item.chemId)

      if (pos !== -1) this.selectedStructures.splice(pos, 1)
      else this.selectedStructures.push(structure)
      structure.checked = pos === -1
    },
    onSelectAllStructures() {
      this.selectedStructures = []
      this.similarity.order.forEach((o) => {
        if (o.show) {
          o.chemical.checked = true
          this.selectedStructures.push(o.chemical)
        }
      })
    },
    onClearSelection() {
      this.selectedStructures.forEach((o) => {
        o.checked = false
      })
      this.selectedStructures = []
    },
    onRemove() {
      this.$store.commit('DESELECT_SOME_STRUCTURES', this.selectedStructures)
      this.similarity = {}
      this.getSimilarity(this.sortS)
    },
  },

  mounted() {
    this.getSimilarity(false)
  },
}
</script>
<style lang="scss" scoped>
.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.ybc11 {
  background-color: #9e9e9e;
}

.ybc10 {
  background-color: #ff5722;
}

.ybc9 {
  background-color: #f57f17;
}

.ybc8 {
  background-color: #f9a825;
}

.ybc7 {
  background-color: #fbc02d;
}

.ybc6 {
  background-color: #fdd835;
}

.ybc5 {
  background-color: #ffee58;
}

.ybc4 {
  background-color: #fff176;
}

.ybc3 {
  background-color: #fff59d;
}

.ybc2 {
  background-color: #fff9c4;
}

.ybc1 {
  background-color: #fffde7;
}

.table-container {
  height: 70vh;
  max-height: 70vh;
  width: calc(100vw - 20px);
  max-width: calc(100vw - 20px);
  overflow: auto;
  position: relative;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  white-space: nowrap;
}

thead th {
  position: sticky;
  top: 0;
  background-color: #f1f1f1;
  z-index: 2;
  border-color: #dee2e6;
}

.sticky-col {
  position: sticky;
  left: 0;
  background-color: #f9f9f9;
  z-index: 1;
}

thead th.sticky-col {
  z-index: 3;
}
</style>
